import { useEffect, useRef, useState } from "react";
import { approveRemoteRun, saveReviewDecision } from "../lib/api";
import { apiUrl } from "../lib/env";
import { formatElapsedSeconds } from "../lib/format";
import { deriveGates, gateBlockers, gateCounts, qaAttemptCount, qaVerified } from "../lib/qa";
import { humanizeToken } from "../lib/status";
import type { ActionState, ChangeItem, RunStatus } from "../lib/types";

export const initialDecisionMessage =
  "Review each proposed edit. Final QA unlocks only after every edit has a decision.";

/**
 * The review + final-QA state machine.
 *
 * All derived state is recomputed from `run` on EVERY render and nothing is memoized across
 * a run identity change. That is deliberate: memoizing QA results across navigation is how
 * stale QA reappears, and a stale "passed" on a governance screen is a lie the operator
 * would act on.
 *
 * Two safety properties this hook must never lose:
 *  1. Any verdict change CLEARS validation, qaLoop and finalCopy and forces status back to
 *     needs_review. A QA result that predates the current decision set is not a QA result.
 *  2. approve() checks the pending count TWICE — once from render-time state and once from
 *     the runRef mirror. The mirror is the authority: an in-flight decision can land between
 *     render and click, and without the second check a run could be approved while an edit
 *     still had no human decision.
 */
export function useReviewMachine(run: RunStatus | undefined, onRun: (run: RunStatus) => void, setToast: (message: string) => void) {
  const [savingDecision, setSavingDecision] = useState("");
  const [decisionMessage, setDecisionMessage] = useState(initialDecisionMessage);
  const [qaState, setQaState] = useState<ActionState>("idle");
  const [qaMessage, setQaMessage] = useState("");
  const [qaStartedAt, setQaStartedAt] = useState<number | null>(null);
  const [qaElapsedSec, setQaElapsedSec] = useState(0);
  const [openChangeIds, setOpenChangeIds] = useState<Record<string, boolean>>({});
  const runRef = useRef<RunStatus | undefined>(run);

  useEffect(() => {
    if (qaState !== "running" || !qaStartedAt) return undefined;
    const updateElapsed = () => setQaElapsedSec(Math.max(1, Math.round((Date.now() - qaStartedAt) / 1000)));
    updateElapsed();
    const timer = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(timer);
  }, [qaStartedAt, qaState]);

  useEffect(() => {
    runRef.current = run;
  }, [run]);

  async function setVerdict(
    changeId: string,
    verdict: ChangeItem["verdict"],
    extra?: { after?: string; reason?: string }
  ) {
    const activeRun = runRef.current || run;
    if (!activeRun) return;
    const label = verdict === "approved" ? "approved" : verdict === "rejected" ? "rejected" : "reset";
    const optimisticRun = {
      ...activeRun,
      status: "needs_review" as const,
      validation: undefined,
      qaLoop: undefined,
      finalCopy: undefined,
      changes: activeRun.changes.map((change) =>
        change.id === changeId
          ? {
              ...change,
              verdict,
              ...(extra?.after != null ? { after: extra.after } : {}),
              ...(extra?.reason != null ? { reviewerNote: extra.reason } : {})
            }
          : change
      )
    };
    runRef.current = optimisticRun;
    onRun(optimisticRun);
    setOpenChangeIds((current) => ({ ...current, [changeId]: false }));
    setQaState("idle");
    setQaMessage("");
    setQaStartedAt(null);
    setQaElapsedSec(0);
    const remaining = optimisticRun.changes.filter((change) => change.verdict === "pending").length;
    // FACT ONLY, NO NEXT ACTION. The next action is owned by the review bar and the action bar,
    // and by nothing else. This line used to end "Run final QA next." and printed it under a red
    // BLOCKED panel — the screen told you to proceed while telling you that you could not.
    setDecisionMessage(remaining ? `Decision saved locally: ${label}. ${remaining} edit${remaining === 1 ? "" : "s"} still need a decision.` : `Decision saved locally: ${label}. All edits are decided.`);
    if (!apiUrl) return;

    setSavingDecision(changeId);
    setDecisionMessage(`Saving ${label} decision...`);
    try {
      const { ok, result } = await saveReviewDecision(
        activeRun.runId,
        changeId,
        verdict,
        extra?.after != null ? { ...extra, source: "human_direct" } : extra
      );
      if (ok && result && "runId" in result) {
        const syncedRun: RunStatus = {
          ...(result as RunStatus),
          status: "needs_review",
          validation: undefined,
          qaLoop: undefined,
          finalCopy: undefined
        };
        runRef.current = syncedRun;
        onRun(syncedRun);
        const nextPending = syncedRun.changes.filter((change) => change.verdict === "pending").length;
        setDecisionMessage(nextPending ? `Saved: ${label}. ${nextPending} edit${nextPending === 1 ? "" : "s"} still need a decision.` : `Saved: ${label}. All edits are decided.`);
        setToast("Review decision saved");
        return;
      }
      const message = result?.message || "Review decision could not be saved";
      setDecisionMessage(message);
      setToast(message);
    } catch {
      setDecisionMessage("Backend did not respond. The decision is visible locally, but sync is not confirmed.");
      setToast("Review decision saved locally. Backend did not respond.");
    } finally {
      setSavingDecision("");
    }
  }

  async function approveRun(pendingCount: number) {
    const activeRun = runRef.current || run;
    if (!activeRun) return;
    const activePendingCount = activeRun.changes.filter((change) => change.verdict === "pending").length;
    // GATE 1: render-time pending count.
    if (pendingCount) {
      setQaState("error");
      setQaStartedAt(null);
      setQaElapsedSec(0);
      setQaMessage(`Final QA is locked until ${pendingCount} edit${pendingCount === 1 ? "" : "s"} are approved or rejected.`);
      return;
    }
    // GATE 2: the runRef mirror, which is authoritative if a decision landed since render.
    if (activePendingCount) {
      setQaState("error");
      setQaStartedAt(null);
      setQaElapsedSec(0);
      setQaMessage(`Final QA is locked until ${activePendingCount} edit${activePendingCount === 1 ? "" : "s"} are approved or rejected.`);
      return;
    }

    if (!apiUrl) {
      const startedAt = Date.now();
      setQaState("running");
      setQaStartedAt(startedAt);
      setQaMessage("Recording local approval...");
      const next = {
        ...activeRun,
        status: "approved" as const,
        stages: activeRun.stages.map((stage) => ({ ...stage, state: "done" as const })),
        message: "Human approval recorded. Export package is ready."
      };
      runRef.current = next;
      onRun(next);
      setQaElapsedSec(Math.max(1, Math.round((Date.now() - startedAt) / 1000)));
      setQaStartedAt(null);
      setQaState("success");
      // HONESTY: local preview runs NO checks. It records a human approval and says so.
      // The old copy said "Final QA passed" and the UI then painted six green ticks on six
      // gates that had never executed. Nothing here may claim a gate passed.
      setDecisionMessage("Approval recorded locally. No QA check has run against this decision set.");
      setQaMessage(
        "Local approval recorded. Recorded locally — not verified by QA. Connect the backend to run the six checks and package the run."
      );
      setToast("Approval recorded locally — not verified by QA");
      return;
    }

    const startedAt = Date.now();
    setQaState("running");
    setQaStartedAt(startedAt);
    setQaMessage("Running final QA. ATLAS is checking approved edits, protected facts, rendering, and package readiness.");
    setToast("Running final QA");
    try {
      const { ok, result } = await approveRemoteRun(activeRun.runId);
      if (result && "runId" in result) {
        runRef.current = result as RunStatus;
        onRun(result as RunStatus);
      }
      if (ok) {
        setQaElapsedSec(Math.max(1, Math.round((Date.now() - startedAt) / 1000)));
        setQaStartedAt(null);
        setQaState("success");
        setDecisionMessage("Final QA passed. Completed edits are collapsed below; open any row to inspect or change it.");
        setQaMessage("Final QA passed. The package is ready to download and send.");
        setToast("Approval recorded after QA");
        return;
      }
      const message = result?.message || "Approval blocked by QA";
      setQaElapsedSec(Math.max(1, Math.round((Date.now() - startedAt) / 1000)));
      setQaStartedAt(null);
      setQaState("error");
      setDecisionMessage("Review decisions are saved. Final QA did not pass.");
      setQaMessage(message);
      setToast(message);
    } catch {
      setQaElapsedSec(Math.max(1, Math.round((Date.now() - startedAt) / 1000)));
      setQaStartedAt(null);
      setQaState("error");
      setDecisionMessage("Review decisions are saved locally, but QA did not complete.");
      setQaMessage("Final QA could not reach the backend. Nothing was approved.");
      setToast("Approval could not reach backend");
    }
  }

  function toggleChangeOpen(changeId: string, open: boolean) {
    setOpenChangeIds((current) => ({ ...current, [changeId]: open }));
  }

  // Everything below is derived fresh from `run` on every render — never memoized.
  if (!run) {
    return { run, qaState, savingDecision, openChangeIds, setVerdict, approveRun, toggleChangeOpen, view: null };
  }

  const approvedCount = run.changes.filter((change) => change.verdict === "approved").length;
  const rejectedCount = run.changes.filter((change) => change.verdict === "rejected").length;
  const decidedCount = run.changes.filter((change) => change.verdict !== "pending").length;
  const pendingCount = run.changes.length - decidedCount;

  /**
   * THE SIX GATES, derived fresh from `run` on every render and never memoized.
   *
   * A gate is "passed" if and only if the backend returned a verdict of "pass" for that
   * specific gate id. Not because the run says "approved". Not because a qaLoop object
   * exists. The Gate type will not even let this file construct a passed gate without the
   * QaLevel that produced it.
   */
  // SAFETY: a QA result computed against a different decision set is not a QA result. While
  // any edit is undecided, every stored verdict — pass OR blocker — is discarded from the
  // display. setVerdict already destroys the stored QA state; this is the render-side twin,
  // so no memo, cache or hoisted context can resurrect a stale badge.
  const staleQa = pendingCount > 0;
  const gates = deriveGates(run, qaState, staleQa);
  const counts = gateCounts(gates);
  const blockers = gateBlockers(gates);

  /** A REAL backend pass. The only thing in this app that may be called "verified". */
  const qaPassed = !staleQa && qaVerified(run);
  /**
   * A human approval with NO QA behind it. This is the supported local-preview path
   * (apiUrl === ""), where approveRun() sets status "approved" and nothing has been checked.
   * It is a true statement about what happened, and it is never dressed up as a pass.
   */
  const recordedLocally = !staleQa && run.status === "approved" && !qaPassed;
  const qaBlocked = !staleQa && (qaState === "error" || run.qaLoop?.aligned === false || counts.failed > 0);
  const qaRunning = qaState === "running";

  /** The one state word the whole screen keys off. Exhaustive, in precedence order. */
  const qaSurfaceState: "running" | "passed" | "blocked" | "recorded" | "locked" | "ready" = qaRunning
    ? "running"
    : qaPassed
      ? "passed"
      : qaBlocked
        ? "blocked"
        : recordedLocally
          ? "recorded"
          : pendingCount
            ? "locked"
            : "ready";

  const qaButtonDisabled = Boolean(pendingCount) || qaRunning;
  const qaButtonText = qaRunning
    ? "Running final QA"
    : pendingCount
      ? "Finish review first"
      : counts.ran || recordedLocally
        ? "Run QA again"
        : "Run final QA";
  /** The plain-language reason a disabled button is disabled, rendered NEXT TO the button. */
  const qaButtonReason = pendingCount
    ? `${pendingCount} edit${pendingCount === 1 ? "" : "s"} still need a decision. Final QA is locked until every edit is approved or rejected.`
    : "";

  const qaTimingText = qaRunning
    ? `Elapsed ${formatElapsedSeconds(qaElapsedSec)}. Most runs complete in 30-90 seconds; repair loops can take longer.`
    : qaElapsedSec && !apiUrl
      ? `Recorded locally in ${formatElapsedSeconds(qaElapsedSec)}. No QA check ran.`
      : qaElapsedSec
        ? `Last QA response took ${formatElapsedSeconds(qaElapsedSec)}.`
        : "Expected time: 30-90 seconds for a normal run.";

  /**
   * ONE headline, stated ONCE, and it states the QA FACT.
   *
   * "Not started" covers both the locked and the ready case, because in both of them the
   * true statement about QA is that it has not run. Whether the button can be pressed is a
   * separate fact, and it is carried by the button and its adjacent reason — not by a second
   * paraphrase of the same state. The old rail printed "Ready for final QA" three times in
   * one column and still never said "not yet".
   */
  const qaTitle = qaRunning
    ? "Final QA — running"
    : qaPassed
      ? "Final QA — passed"
      : qaBlocked
        ? "Final QA — did not pass"
        : recordedLocally
          ? "Recorded locally — not verified by QA"
          : "Final QA — not started";

  const qaActionMessage = qaMessage || (qaRunning
    ? "ATLAS is checking the approved edit set. Keep this page open."
    : qaPassed
      ? "Every required check returned a pass. The package step is ready."
      : qaBlocked
        ? `Export is blocked${run.qaLoop?.terminalReason ? `: ${humanizeToken(run.qaLoop.terminalReason)}` : ""}. The checks that did not pass are listed below.`
        : recordedLocally
          ? "A human approval is recorded for this run. No QA check has executed against it."
          : pendingCount
            ? `Decide ${pendingCount} remaining edit${pendingCount === 1 ? "" : "s"} to unlock QA.`
            : "Run the final check to verify protected facts, editable blocks, research rigor, patch application, rendering and reviewer sign-off.");

  /**
   * THE ONE STATE SENTENCE, for the sticky review bar. It is the ONLY place the run state is
   * stated on this screen. The old rail said it four times: an h2, a StatusPill, a qa-message and
   * a "what needs attention" card, two of which used the identical clause 200px apart.
   *
   * The blocked branch states a COUNT only when there is a count to state. `qaBlocked` is also
   * true when the backend never answered (qaState === "error" with no gate verdicts at all), and
   * "0 of 6 checks did not pass" would be a number invented to fill a sentence.
   *
   * These strings are deliberately NOT byte-identical to the QA panel's headline. Two nodes on
   * one screen carrying the same exact sentence is the restatement this rebuild exists to kill.
   */
  const qaBarSentence = qaRunning
    ? "Final QA running"
    : qaPassed
      ? `Final QA passed — ${counts.passed} of ${counts.total} checks`
      : qaBlocked
        ? counts.failed
          ? `Export blocked — ${counts.failed} of ${counts.total} checks did not pass`
          : "Export blocked — final QA did not complete"
        : recordedLocally
          ? "Recorded locally — no check has run"
          : pendingCount
            ? `${pendingCount} edit${pendingCount === 1 ? "" : "s"} need${pendingCount === 1 ? "s" : ""} a decision`
            : "All edits decided — final QA not run";

  const reviewGateMessage = pendingCount
    ? `${pendingCount} edit${pendingCount === 1 ? "" : "s"} still need a decision before final QA.`
    : `${approvedCount} approved, ${rejectedCount} rejected.`;

  /**
   * REVIEW PROGRESS IS PURELY FACTUAL AND EMITS NO NEXT ACTION.
   * It used to fall through to "All edits are decided. Run final QA next." on a run loaded from
   * storage — including a BLOCKED one, where it printed directly under the red blocked panel and
   * told the operator to proceed while the screen told them they could not.
   */
  const decisionDisplayMessage = decisionMessage !== initialDecisionMessage || savingDecision
    ? decisionMessage
    : pendingCount
      ? initialDecisionMessage
      : "All edits are decided.";

  /**
   * A COUNTER FOR A THING WITH NO ATTEMPTS IS NOT A FACT. "Final QA attempt: Not started" printed
   * an attempt row for a run that had never attempted anything. It renders only when the backend
   * actually reports an attempt budget; otherwise the screen says nothing, which is the truth.
   */
  const qaAttemptLabel = run.qaLoop?.maxAttempts
    ? `Final QA attempt ${qaAttemptCount(run.qaLoop) || 1} of ${run.qaLoop.maxAttempts}`
    : "";

  const approvedChanges = run.changes.filter((change) => change.verdict === "approved");
  const rejectedChanges = run.changes.filter((change) => change.verdict === "rejected");

  return {
    run,
    qaState,
    qaMessage,
    qaElapsedSec,
    savingDecision,
    openChangeIds,
    setVerdict,
    approveRun,
    toggleChangeOpen,
    view: {
      approvedCount,
      rejectedCount,
      decidedCount,
      pendingCount,
      gates,
      counts,
      blockers,
      qaPassed,
      qaBlocked,
      qaRunning,
      recordedLocally,
      qaSurfaceState,
      qaButtonDisabled,
      qaButtonText,
      qaButtonReason,
      qaTimingText,
      qaTitle,
      qaActionMessage,
      qaAttemptLabel,
      qaBarSentence,
      reviewGateMessage,
      decisionDisplayMessage,
      approvedChanges,
      rejectedChanges
    }
  };
}
